
import React from 'react';

const Navigation = ({
  currentUser,
  activeTab,
  onTabChange,
  onLogout,
  onShowUserManagement,
  sidebarOpen,
  onToggleSidebar,
}) => {
  return (
    <div
      className={`fixed inset-y-0 left-0 z-50 w-64 bg-gradient-to-b from-gray-900 to-gray-800 transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 shadow-2xl`}
    >
      {/* Header */}
      <div className="flex items-center justify-between h-16 px-4 bg-gray-900 border-b border-gray-700">
        <div className="flex items-center">
          <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mr-3 shadow-md">
            <svg
              className="w-5 h-5 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
          <span className="text-white font-bold text-lg hidden sm:block">
            Blend Optimizer
          </span>
          <span className="text-white font-bold text-lg sm:hidden">BO</span>
        </div>

        {/* Close Button (Mobile only) */}
        <button
          onClick={onToggleSidebar}
          className="lg:hidden text-gray-400 hover:text-white transition-all duration-200"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Navigation Menu */}
      <nav className="mt-6 px-3 sm:px-4 space-y-2 overflow-y-auto h-[calc(100vh-8rem)] scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-800">
        {[
          { key: 'optimizer', label: 'Blend Optimizer', short: 'Optimizer', icon: 'chart-bar' },
          { key: 'components', label: 'Components', short: 'Components', icon: 'grid' },
          { key: 'properties', label: 'Properties', short: 'Properties', icon: 'settings' },
          { key: 'model-basis', label: 'Model Settings', short: 'Setting', icon: 'shield-check' },
          { key: 'optimization-results', label: 'Saved Results', short: 'Results', icon: 'check-circle' },
        ].map((item) => (
          <button
            key={item.key}
            onClick={() => onTabChange(item.key)}
            className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
              activeTab === item.key
                ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                : 'text-gray-300 hover:bg-gray-700 hover:text-white'
            }`}
          >
            <span className="mr-3 flex-shrink-0">
              {item.icon === 'chart-bar' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19V9a2 2 0 012-2h2a2 2 0 012 2v10M5 19V5a2 2 0 012-2h2a2 2 0 012 2v14m8-4V9a2 2 0 00-2-2h-2a2 2 0 00-2 2"
                  />
                </svg>
              )}
              {item.icon === 'grid' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h4v4H4zM4 14h4v4H4zM14 6h4v4h-4zM14 14h4v4h-4z"
                  />
                </svg>
              )}
              {item.icon === 'settings' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8a4 4 0 100 8 4 4 0 000-8z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 12a8 8 0 0116 0A8 8 0 014 12z"
                  />
                </svg>
              )}
              {item.icon === 'shield-check' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 2l7 4v6a7 7 0 01-7 7 7 7 0 01-7-7V6l7-4z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4"
                  />
                </svg>
              )}
              {item.icon === 'check-circle' && (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              )}
            </span>
            <span className="hidden sm:block">{item.label}</span>
            <span className="sm:hidden">{item.short}</span>
          </button>
        ))}

        {/* Admin-only User Management */}
        {currentUser?.role === 'admin' && (
          <button
            onClick={onShowUserManagement}
            className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white rounded-xl transition-all duration-200"
          >
            <svg
              className="w-5 h-5 mr-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 11c0-2.21 1.79-4 4-4s4 1.79 4 4M4 11c0-2.21 1.79-4 4-4s4 1.79 4 4M12 21c0-2.21 1.79-4 4-4s4 1.79 4 4M4 21c0-2.21 1.79-4 4-4s4 1.79 4 4"
              />
            </svg>
            <span className="hidden sm:block">User Management</span>
            <span className="sm:hidden">Users</span>
          </button>
        )}

        {/* Logout */}
        <button
          onClick={onLogout}
          className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-300 hover:bg-gray-700 hover:text-white rounded-xl transition-all duration-200"
        >
          <svg
            className="w-5 h-5 mr-3 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
            />
          </svg>
          <span className="hidden sm:block">Logout</span>
          <span className="sm:hidden">Logout</span>
        </button>
      </nav>

      {/* Footer User Info */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700 bg-gray-900/60 backdrop-blur-md">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-white font-medium text-sm truncate">
              {currentUser?.username}
            </div>
            <div className="text-gray-400 text-xs capitalize">
              {currentUser?.role}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navigation;
